import { FullProject, Task } from '../types';
import { isCommProject } from '../utils/idNormalizer';
import { isProjectEditableByUser } from '../utils/projectPermissions';
import {
  getContactVorgangDisplayName,
  resolveEffectiveProjectForTask,
} from '../components/communication/personModel';
import {
  hasTaskDate,
  isContactVorgangView,
  isFolderTaskView,
  isIdeaTaskView,
  isPersonContactView,
} from './taskGuards';
import { normalizeTaskStatus, normalizeTaskType } from './taskStatusAndTypes';
import { matchesTaskFilter, TaskFilterCriteria } from './taskFilterEngine';

/**
 * Orders a list of projects so that real user projects come before `comm_internal`.
 * This guarantees that if a task ID appears in both a real project and `comm_internal`,
 * deduplication always prefers the real project instance.
 */
export function orderProjectsRealFirst(projects: FullProject[]): FullProject[] {
  return [...projects].sort((a, b) => (isCommProject(a.id) ? 1 : 0) - (isCommProject(b.id) ? 1 : 0));
}

/**
 * Computes the 2-letter uppercase project badge from a project name.
 */
export function getProjectShortBadge(name?: string): string {
  if (!name) return '??';
  const clean = name.trim();
  if (!clean) return '??';
  const parts = clean.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return clean.substring(0, 2).toUpperCase();
}

export interface AggregatedWorkspaceTaskContext {
  task: Task;
  sourceProject: FullProject;
  effectiveProject: FullProject;
  isFromComm: boolean;
  displayName: string;
  effectiveProjectName: string;
}

export interface AggregatedWorkspaceTaskEntry extends AggregatedWorkspaceTaskContext {
  normType: string;
  normStatus: string;
  isReadOnly: boolean;
  globalId: string;
  projectBadge: string | undefined;
}

/**
 * Resolves effective project attribution and display name for a task coming from `sourceProject`
 * within `allProjects`.
 */
export function resolveAggregatedTaskContext(
  task: Task,
  sourceProject: FullProject,
  allProjects: FullProject[]
): AggregatedWorkspaceTaskContext {
  const isFromComm = isCommProject(sourceProject.id);
  const effectiveProject = resolveEffectiveProjectForTask(task, sourceProject, allProjects);
  const effectiveProjectName = isCommProject(effectiveProject.id)
    ? 'Kommunikation'
    : effectiveProject.name || sourceProject.name || '';
  const displayName = getContactVorgangDisplayName(task, isFromComm) || task.name || '';

  return {
    task,
    sourceProject,
    effectiveProject,
    isFromComm,
    displayName,
    effectiveProjectName,
  };
}

/**
 * Checks whether a task should be excluded from standard task/schedule views
 * because it is a pure address-book contact or an undated Contact-Vorgang.
 */
export function isExcludedContactOrUndatedVorgang(task: Task, isFromComm = false): boolean {
  if (isPersonContactView(task)) return true;
  if (isContactVorgangView(task, isFromComm) && !hasTaskDate(task)) return true;
  return false;
}

export interface AggregateWorkspaceTasksOptions {
  /** Source projects to iterate (will be ordered real-projects-first via `orderProjectsRealFirst`). */
  sourceProjects: FullProject[];
  /** Full list of projects used to resolve `effectiveProject` for `comm_internal` tasks. Defaults to `sourceProjects`. */
  allProjects?: FullProject[];
  /** If provided, filters out projects/tasks not belonging to this workspace. */
  activeWorkspace?: 'professional' | 'private';
  /** Whether archived projects should be included at the project level (default: true when omitted). */
  showArchivedProjects?: boolean;
  /** Optional predicate to skip specific projects (e.g. system inbox projects). */
  shouldIncludeProject?: (project: FullProject, isFromComm: boolean) => boolean;
  /** Optional set/array of selected project IDs (matches either `effectiveProject.id` or `sourceProject.id`). */
  selectedProjectIds?: Iterable<string>;
  /** Exclude `idea`, `folder`, and `topic` tasks (default: true). */
  excludeIdeasAndFolders?: boolean;
  /** Require `start` or `end` date on tasks (default: false). */
  requireDates?: boolean;
  /** Deduplicate tasks by `String(task.id)` across projects, preferring real projects over `comm_internal` (default: true). */
  deduplicateByTaskId?: boolean;
  /** Optional user ID for read-only permission calculation (`isProjectEditableByUser`). */
  currentUserId?: string;
  /** Optional user email for read-only permission calculation (`isProjectEditableByUser`). */
  userEmail?: string;
  /** Optional filter bar criteria (`matchesTaskFilter`). */
  filterCriteria?: TaskFilterCriteria;
}

/**
 * Single Source of Truth for cross-project task aggregation across Global Kanban, Global Gantt,
 * and Global Calendar:
 * 1. Orders `sourceProjects` so real projects come before `comm_internal`.
 * 2. Applies project-level archive, workspace, and custom predicates.
 * 3. Excludes pure contacts, undated Contact-Vorgänge, and optionally ideas/folders or undated tasks.
 * 4. Resolves effective project attribution (`comm_internal` -> target project via `projectIds`).
 * 5. Enforces workspace isolation on the resolved `effectiveProject`.
 * 6. Deduplicates by `String(task.id)` so tasks mirrored in `comm_internal` never appear twice.
 * 7. Computes normalized type/status, read-only permissions, and optional filter matching.
 */
export function aggregateWorkspaceTasks(
  options: AggregateWorkspaceTasksOptions
): AggregatedWorkspaceTaskEntry[] {
  const {
    sourceProjects,
    allProjects = sourceProjects,
    activeWorkspace,
    showArchivedProjects = true,
    shouldIncludeProject,
    selectedProjectIds,
    excludeIdeasAndFolders = true,
    requireDates = false,
    deduplicateByTaskId = true,
    currentUserId,
    userEmail,
    filterCriteria,
  } = options;

  const orderedProjects = orderProjectsRealFirst(sourceProjects);
  const seenTaskIds = new Set<string>();
  const selectedSet = selectedProjectIds ? new Set(selectedProjectIds) : null;
  const entries: AggregatedWorkspaceTaskEntry[] = [];

  for (const sourceProject of orderedProjects) {
    const isFromComm = isCommProject(sourceProject.id);
    if (!showArchivedProjects && sourceProject.archived) continue;
    if (activeWorkspace && !isFromComm && (sourceProject.workspace || 'professional') !== activeWorkspace) {
      continue;
    }
    if (shouldIncludeProject && !shouldIncludeProject(sourceProject, isFromComm)) {
      continue;
    }

    const projectTasks = sourceProject.tasks || [];
    for (const task of projectTasks) {
      if (excludeIdeasAndFolders && (isIdeaTaskView(task) || isFolderTaskView(task))) {
        continue;
      }
      if (requireDates && !hasTaskDate(task)) {
        continue;
      }
      if (isExcludedContactOrUndatedVorgang(task, isFromComm)) {
        continue;
      }

      const context = resolveAggregatedTaskContext(task, sourceProject, allProjects);
      const { effectiveProject, displayName, effectiveProjectName } = context;

      if (activeWorkspace) {
        const effectiveWs = effectiveProject.workspace || task.workspace || 'professional';
        if (effectiveWs !== activeWorkspace) continue;
      }

      if (selectedSet && !selectedSet.has(effectiveProject.id) && !selectedSet.has(sourceProject.id)) {
        continue;
      }

      const idKey = String(task.id);
      if (deduplicateByTaskId && seenTaskIds.has(idKey)) {
        continue;
      }

      const normType = normalizeTaskType(task.type || 'task');
      const normStatus = normalizeTaskStatus(task.status || 'open');

      if (filterCriteria) {
        const matches = matchesTaskFilter(
          task,
          {
            ...filterCriteria,
            projectName: filterCriteria.projectName ?? effectiveProjectName,
          },
          {
            normStatus,
            normType,
            effectiveName: displayName,
          }
        );
        if (!matches) continue;
      }

      if (deduplicateByTaskId) {
        seenTaskIds.add(idKey);
      }

      const isEditable = isProjectEditableByUser(effectiveProject, currentUserId, userEmail);
      const projectBadge =
        effectiveProject.id !== 'inbox_inforum' ? getProjectShortBadge(effectiveProjectName) : undefined;

      entries.push({
        ...context,
        normType,
        normStatus,
        isReadOnly: !isEditable,
        globalId: `${sourceProject.id}_${task.id}`,
        projectBadge,
      });
    }
  }

  return entries;
}
